import { useId, useMemo, useState, type ComponentType, type ReactNode } from "react";
import {
  CalendarClock,
  CalendarPlus,
  CheckCircle2,
  Clock3,
  Download,
  HardDrive,
  Images,
  Inbox,
  Info,
  Layers3,
  ListChecks,
  Palette,
  Paperclip,
  FileSpreadsheet,
  Shield,
  Users,
  X,
  Zap,
} from "lucide-react";
import { MailModalOverlay } from "@/features/shared/MailModalOverlay";
import { MailTooltip } from "@/features/shared/MailTooltip";
import { MailHeaderIconButton } from "@/features/shell/MailHeaderIconButton";
import { cn } from "@/lib/cn";

type GuideFeatureDef = {
  key: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  iconTint: string;
  title: string;
  text: string;
};

const GUIDE_ICON = {
  cache: "bg-[color-mix(in_srgb,var(--mail-accent)_14%,transparent)] text-[var(--mail-accent)]",
  sync: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200",
  schedule: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  attach: "bg-cyan-100 text-cyan-800 dark:bg-cyan-500/15 dark:text-cyan-200",
  suite: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
  accounts: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-200",
  select: "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300",
} as const;

function GuideFeature({
  icon: Icon,
  iconTint,
  title,
  children,
}: {
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  iconTint: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <article className="flex gap-3 rounded-xl border border-[var(--mail-border)] bg-[var(--mail-surface-muted)]/70 p-3">
      <span
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset ring-black/5 dark:ring-white/10",
          iconTint
        )}
        aria-hidden
      >
        <Icon className="h-4 w-4" strokeWidth={2} />
      </span>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold leading-snug text-[var(--mail-text)]">{title}</h3>
        <p className="mt-1 text-xs leading-relaxed text-[var(--mail-muted)]">{children}</p>
      </div>
    </article>
  );
}

function useMailGuideFeatures(): GuideFeatureDef[] {
  return useMemo(
    () => [
      {
        key: "personalize",
        icon: Palette,
        iconTint: GUIDE_ICON.select,
        title: "Personnalisation",
        text: "Changez le thème, la couleur d’accent et le son de notification depuis le menu en haut à droite.",
      },
      {
        key: "instant",
        icon: Zap,
        iconTint: GUIDE_ICON.cache,
        title: "Lecture immédiate",
        text: "Vos messages sont préparés à l’avance pour s’ouvrir tout de suite, sans temps d’attente inutile.",
      },
      {
        key: "sync",
        icon: Inbox,
        iconTint: GUIDE_ICON.sync,
        title: "Nouveaux messages",
        text: "Les nouveaux mails arrivent automatiquement. Vous pouvez aussi actualiser à tout moment pour forcer la mise à jour.",
      },
      {
        key: "scheduled",
        icon: CalendarClock,
        iconTint: GUIDE_ICON.schedule,
        title: "Envois programmés",
        text: "Rédigez maintenant et choisissez l’heure d’envoi : le message part au moment prévu.",
      },
      {
        key: "snooze",
        icon: Clock3,
        iconTint: GUIDE_ICON.schedule,
        title: "Mettre en attente",
        text: "Reportez un message : il quitte la boîte et revient au moment que vous avez choisi.",
      },
      {
        key: "multiselect",
        icon: ListChecks,
        iconTint: GUIDE_ICON.select,
        title: "Sélection multiple",
        text: "Cochez plusieurs messages pour les déplacer, supprimer ou mettre en attente d’un seul geste. L’icône trombone sélectionne d’un coup tous les mails avec pièces jointes.",
      },
      {
        key: "bulk-attachments",
        icon: Download,
        iconTint: GUIDE_ICON.attach,
        title: "Extraire les pièces jointes en masse",
        text: "Avec plusieurs messages cochés, téléchargez toutes les pièces jointes d’un coup, ou enregistrez-les directement dans le Coffre — idéal pour traiter une série de mails sans les ouvrir un par un.",
      },
      {
        key: "attach-disk",
        icon: HardDrive,
        iconTint: GUIDE_ICON.attach,
        title: "Joindre depuis votre appareil",
        text: "Ajoutez des fichiers depuis votre ordinateur ou votre téléphone lors de la rédaction.",
      },
      {
        key: "attach-suite",
        icon: Paperclip,
        iconTint: GUIDE_ICON.suite,
        title: "Joindre depuis le Coffre",
        text: "Ajoutez aussi des fichiers déjà stockés dans le Coffre, sans quitter Mail.",
      },
      {
        key: "office-preview",
        icon: FileSpreadsheet,
        iconTint: GUIDE_ICON.select,
        title: "Aperçu Word, Excel, PowerPoint",
        text: "Les documents Office s’ouvrent en lecture dans le panneau d’aperçu, comme une photo ou un PDF.",
      },
      {
        key: "save-suite",
        icon: Images,
        iconTint: GUIDE_ICON.suite,
        title: "Enregistrer dans le Coffre",
        text: "Depuis un message ouvert, ou via la sélection multiple : rangez les pièces jointes dans le Coffre. Le clic droit d’un message propose « Archiver dans Coffre » : le mail est enregistré en .eml dans le dossier choisi.",
      },
      {
        key: "ics-agenda",
        icon: CalendarPlus,
        iconTint: GUIDE_ICON.schedule,
        title: "Enregistrer dans l’agenda",
        text: "Une invitation .ics affiche en haut du message un bloc « Ajouter à mon agenda ». Le titre, la date et le lieu s’affichent, vous choisissez l’agenda, puis le rendez-vous est créé. L’icône calendrier reste aussi sur la pièce jointe. Un second clic ne crée pas de doublon. Les séries et les annulations restent en fichier joint.",
      },
      {
        key: "multicompte",
        icon: Users,
        iconTint: GUIDE_ICON.accounts,
        title: "Plusieurs boîtes mail",
        text: "Connectez plusieurs comptes et passez de l’un à l’autre dans la même application.",
      },
      {
        key: "unified",
        icon: Layers3,
        iconTint: GUIDE_ICON.accounts,
        title: "Unifiée",
        text: "Une vue unique regroupe les nouveaux messages de toutes vos boîtes pour tout traiter au même endroit.",
      },
      {
        key: "cache",
        icon: CheckCircle2,
        iconTint: GUIDE_ICON.cache,
        title: "Indicateur à côté du message",
        text: "Nuage : pas encore prêt · coche verte : prêt à ouvrir. Les nouveaux non lus se préparent en arrière-plan avant d’apparaître dans la liste.",
      },
      {
        key: "suite-badges",
        icon: Shield,
        iconTint: GUIDE_ICON.suite,
        title: "Coffre",
        text: "Quand le Coffre est fermé, enregistrer une pièce jointe ouvre le mot de passe maître dans Mail.",
      },
    ],
    []
  );
}

function MailGuideContent() {
  const features = useMailGuideFeatures();

  return (
    <div className="space-y-4">
      <p className="text-sm leading-relaxed text-[var(--mail-muted)]">
        <strong className="font-semibold text-[var(--mail-text)]">Aurélia Mail</strong> regroupe vos
        boîtes mail : lecture rapide, envois programmés, mise en attente, plusieurs comptes,
        extraction des pièces jointes en masse, personnalisation (thème et son), et un lien direct
        avec le Coffre — le tout dans Aurélia Suite.
      </p>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {features.map((f) => (
          <GuideFeature key={f.key} icon={f.icon} iconTint={f.iconTint} title={f.title}>
            {f.text}
          </GuideFeature>
        ))}
      </div>
    </div>
  );
}

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export function MailGuideDialog({ open, onClose }: DialogProps) {
  const titleId = useId();

  return (
    <MailModalOverlay
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      panelClassName="flex max-h-[min(85dvh,calc(100dvh-1.5rem),40rem)] w-full max-w-[min(56rem,calc(100vw-1.5rem))] flex-col overflow-hidden"
      animation="fade"
    >
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--mail-border)] px-4 py-3.5">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--mail-accent)]">
            Aurélia Mail
          </p>
          <h2 id={titleId} className="mt-0.5 text-lg font-semibold text-[var(--mail-text)] sm:text-xl">
            Principes & fonctionnement
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-lg p-1.5 text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]"
          aria-label="Fermer"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5">
        <MailGuideContent />
      </div>

      <div className="shrink-0 border-t border-[var(--mail-border)] px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-xl border border-[var(--mail-border)] bg-[var(--mail-surface-muted)] px-4 py-2 text-sm font-semibold text-[var(--mail-text)] transition hover:bg-[var(--mail-surface-hover)]"
        >
          Fermer
        </button>
      </div>
    </MailModalOverlay>
  );
}

/** Bouton (i) à côté du badge Coffre — guide fonctionnalités Mail. */
export function MailGuideModal() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <MailTooltip label="Principes d’Aurélia Mail" side="bottom" compact>
        <MailHeaderIconButton label="Principes d’Aurélia Mail" onClick={() => setOpen(true)}>
          <Info className="h-4 w-4" strokeWidth={2} />
        </MailHeaderIconButton>
      </MailTooltip>
      <MailGuideDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
